import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { getCommerceId } from '@/lib/ownership';
import type { ZifekUser } from '@/hooks/useAuth';
import { uploadMediaFile } from '@/hooks/useUpload';

// ─────────────────────────────────────────────────────────────
// MyLinksSettings — personnalisation de la page publique /mylinks.
// Permet de choisir l'image de fond (bannière), un titre d'accroche,
// une offre mise en avant et des boutons libres (liens personnalisés).
// ─────────────────────────────────────────────────────────────

export type MyLinksFeatureKind = '' | 'product' | 'service' | 'annonce';

export interface MyLinksButton {
  label: string;
  url: string;
  icon: string;
}

export interface MyLinksConfig {
  cover_url: string;
  headline: string;
  featured_kind: MyLinksFeatureKind;
  featured_id: number;
  buttons: MyLinksButton[];
}

export const MYLINKS_ICON_CHOICES = [
  'ri-link',
  'ri-shopping-bag-3-line',
  'ri-service-line',
  'ri-calendar-check-line',
  'ri-whatsapp-line',
  'ri-instagram-line',
  'ri-facebook-circle-line',
  'ri-tiktok-line',
  'ri-map-pin-line',
  'ri-phone-line',
  'ri-mail-line',
  'ri-global-line',
  'ri-video-line',
  'ri-ticket-2-line',
  'ri-store-2-line',
  'ri-file-list-3-line',
];

const MAX_BUTTONS = 8;

interface Option {
  id: number;
  label: string;
}

export default function MyLinksSettings({ user }: { user: ZifekUser | null }) {
  const [config, setConfig] = useState<MyLinksConfig>({
    cover_url: '',
    headline: '',
    featured_kind: '',
    featured_id: 0,
    buttons: [],
  });
  const [products, setProducts] = useState<Option[]>([]);
  const [services, setServices] = useState<Option[]>([]);
  const [annonces, setAnnonces] = useState<Option[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const merchantId = getCommerceId(user);

  const load = useCallback(async () => {
    if (!merchantId) return;
    setLoading(true);
    try {
      const [cfgRes, prodRes, servRes, annRes] = await Promise.all([
        supabase
          .from('merchant_mylinks')
          .select('cover_url, headline, featured_kind, featured_id, buttons')
          .eq('merchant_id', merchantId)
          .maybeSingle(),
        supabase
          .from('product_items')
          .select('id, name')
          .eq('idcommerce', merchantId)
          .neq('status', 'draft')
          .order('id', { ascending: false })
          .limit(60),
        supabase
          .from('nospartenairesservices')
          .select('id, titre')
          .or(`owner.eq.${merchantId},idcommerce.eq.${merchantId}`)
          .order('id', { ascending: false })
          .limit(60),
        supabase
          .from('annonce')
          .select('id, titre')
          .eq('idvendeur', merchantId)
          .order('id', { ascending: false })
          .limit(60),
      ]);

      const row = cfgRes.data as {
        cover_url?: string | null;
        headline?: string | null;
        featured_kind?: string | null;
        featured_id?: number | null;
        buttons?: unknown;
      } | null;

      if (row) {
        const rawButtons = Array.isArray(row.buttons) ? (row.buttons as MyLinksButton[]) : [];
        setConfig({
          cover_url: String(row.cover_url || ''),
          headline: String(row.headline || ''),
          featured_kind: (row.featured_kind || '') as MyLinksFeatureKind,
          featured_id: Number(row.featured_id) || 0,
          buttons: rawButtons
            .filter((b) => b && String(b.label || '').trim())
            .map((b) => ({ label: String(b.label), url: String(b.url || ''), icon: String(b.icon || 'ri-link') })),
        });
      }

      setProducts(((prodRes.data || []) as { id: number; name: string }[]).map((p) => ({ id: p.id, label: p.name })));
      setServices(((servRes.data || []) as { id: number; titre: string }[]).map((s) => ({ id: s.id, label: s.titre })));
      setAnnonces(((annRes.data || []) as { id: number; titre: string }[]).map((a) => ({ id: a.id, label: a.titre })));
    } catch {
      setMessage("Impossible de charger votre personnalisation MyLinks.");
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => { void load(); }, [load]);

  const uploadCover = async (file: File) => {
    setMessage(null);
    setUploading(true);
    try {
      const url = await uploadMediaFile(file, 'mylinks');
      if (url) setConfig((c) => ({ ...c, cover_url: url }));
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "L'image n'a pas pu être envoyée.");
    } finally {
      setUploading(false);
    }
  };

  const updateButton = (index: number, patch: Partial<MyLinksButton>) => {
    setConfig((c) => ({
      ...c,
      buttons: c.buttons.map((b, i) => (i === index ? { ...b, ...patch } : b)),
    }));
  };

  const addButton = () => {
    if (config.buttons.length >= MAX_BUTTONS) return;
    setConfig((c) => ({ ...c, buttons: [...c.buttons, { label: '', url: '', icon: 'ri-link' }] }));
  };

  const removeButton = (index: number) => {
    setConfig((c) => ({ ...c, buttons: c.buttons.filter((_, i) => i !== index) }));
  };

  const save = async () => {
    if (!merchantId) return;
    setSaving(true);
    setMessage(null);
    try {
      const cleanButtons = config.buttons
        .map((b) => ({ label: b.label.trim(), url: b.url.trim(), icon: b.icon || 'ri-link' }))
        .filter((b) => b.label && b.url)
        .slice(0, MAX_BUTTONS);

      const { error } = await supabase
        .from('merchant_mylinks')
        .upsert({
          merchant_id: merchantId,
          cover_url: config.cover_url || null,
          headline: config.headline.trim() || null,
          featured_kind: config.featured_kind || null,
          featured_id: config.featured_kind && config.featured_id ? config.featured_id : null,
          buttons: cleanButtons,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'merchant_id' });
      if (error) throw error;
      setConfig((c) => ({ ...c, buttons: cleanButtons }));
      setMessage('Personnalisation enregistrée. Votre page MyLinks est à jour.');
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "L'enregistrement a échoué.");
    } finally {
      setSaving(false);
    }
  };

  const featuredOptions = config.featured_kind === 'product'
    ? products
    : config.featured_kind === 'service'
      ? services
      : config.featured_kind === 'annonce'
        ? annonces
        : [];

  if (loading) {
    return (
      <div className="mt-4 flex items-center justify-center py-8 rounded-lg border border-background-200/70 bg-background-50">
        <i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-lg border border-background-200/70 bg-background-50 p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
          <i className="ri-palette-line"></i>
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-900">Personnaliser ma page MyLinks</p>
          <p className="text-[11px] text-foreground-500">Image de fond, titre, offre mise en avant et boutons libres</p>
        </div>
        <Link
          to="/dashboard/mylinks-stats"
          className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-full bg-background-100 text-xs font-medium text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap no-underline"
        >
          <i className="ri-bar-chart-box-line"></i>
          Voir les statistiques
        </Link>
      </div>

      {/* Image de fond */}
      <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-3 items-start">
        <div className="w-full h-24 rounded-md overflow-hidden bg-background-100 flex items-center justify-center">
          {config.cover_url ? (
            <img src={config.cover_url} alt="Image de fond MyLinks" className="w-full h-full object-cover" />
          ) : (
            <i className="ri-image-line text-2xl text-foreground-300"></i>
          )}
        </div>
        <div className="min-w-0">
          <label className="block text-xs font-medium text-foreground-600 mb-1">Image de fond (bannière)</label>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-background-100 border border-background-200/70 text-xs font-medium text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {uploading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-2-line"></i>}
              {config.cover_url ? 'Changer' : 'Ajouter une image'}
            </button>
            {config.cover_url && (
              <button
                type="button"
                onClick={() => setConfig((c) => ({ ...c, cover_url: '' }))}
                className="text-xs text-foreground-400 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                Retirer
              </button>
            )}
          </div>
          <div className="mt-2">
            <label className="block text-xs font-medium text-foreground-600 mb-1">Titre d&apos;accroche</label>
            <input
              type="text"
              value={config.headline}
              onChange={(e) => setConfig((c) => ({ ...c, headline: e.target.value }))}
              maxLength={90}
              placeholder="Ex : Beauté & soins à Fès — réservez en ligne"
              className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
            />
          </div>
        </div>
      </div>

      {/* Offre mise en avant */}
      <div className="mt-4 pt-3 border-t border-background-200/70">
        <label className="block text-xs font-medium text-foreground-600 mb-1">Offre mise en avant</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <select
            value={config.featured_kind}
            onChange={(e) => setConfig((c) => ({ ...c, featured_kind: e.target.value as MyLinksFeatureKind, featured_id: 0 }))}
            className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 cursor-pointer focus:outline-none focus:border-primary-300"
          >
            <option value="">Aucune</option>
            {products.length > 0 && <option value="product">Produit</option>}
            {services.length > 0 && <option value="service">Service</option>}
            {annonces.length > 0 && <option value="annonce">Annonce</option>}
          </select>
          {config.featured_kind && (
            <select
              value={config.featured_id || 0}
              onChange={(e) => setConfig((c) => ({ ...c, featured_id: Number(e.target.value) }))}
              className="w-full px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 cursor-pointer focus:outline-none focus:border-primary-300"
            >
              <option value={0}>Choisir…</option>
              {featuredOptions.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
          )}
        </div>
        <p className="text-[11px] text-foreground-400 mt-1.5">
          Cette offre apparaît en grand sur votre page MyLinks avec son prix et un bouton direct.
        </p>
      </div>

      {/* Boutons libres */}
      <div className="mt-4 pt-3 border-t border-background-200/70">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-xs font-medium text-foreground-600">Boutons libres</label>
          <span className="text-[11px] text-foreground-400">{config.buttons.length}/{MAX_BUTTONS}</span>
        </div>
        <div className="space-y-2">
          {config.buttons.map((b, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-center">
              <input
                type="text"
                value={b.label}
                onChange={(e) => updateButton(i, { label: e.target.value })}
                maxLength={40}
                placeholder="Libellé (ex : Réserver par WhatsApp)"
                className="w-full min-w-0 px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
              />
              <input
                type="url"
                value={b.url}
                onChange={(e) => updateButton(i, { url: e.target.value })}
                placeholder="https://…"
                className="w-full min-w-0 px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
              />
              <div className="flex items-center gap-1">
                <select
                  value={b.icon}
                  onChange={(e) => updateButton(i, { icon: e.target.value })}
                  title="Icône du bouton"
                  aria-label="Icône du bouton"
                  className="px-2 py-2 rounded-lg border border-background-200/70 bg-background-50 text-xs text-foreground-700 cursor-pointer focus:outline-none focus:border-primary-300"
                >
                  {MYLINKS_ICON_CHOICES.map((ic) => (
                    <option key={ic} value={ic}>{ic.replace('ri-', '').replace('-line', '')}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => removeButton(i)}
                  title="Supprimer ce bouton"
                  className="w-9 h-9 rounded-lg flex items-center justify-center text-foreground-400 hover:text-red-500 hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
        {config.buttons.length < MAX_BUTTONS && (
          <button
            type="button"
            onClick={addButton}
            className="mt-2 flex items-center gap-1.5 px-3 py-2 rounded-full bg-background-100 text-xs font-medium text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Ajouter un bouton
          </button>
        )}
      </div>

      {message && (
        <p className={`mt-3 text-xs ${message.includes('enregistrée') ? 'text-accent-700' : 'text-red-600'}`}>{message}</p>
      )}

      <button
        type="button"
        onClick={save}
        disabled={saving}
        className="mt-3 flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
      >
        {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-3-line"></i>}
        Enregistrer MyLinks
      </button>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void uploadCover(file);
          e.target.value = '';
        }}
      />
    </div>
  );
}